import {
  Box,
  Modal,
  Text,
  Input,
  FileUpload,
  AddItem,
  Dropzone,
  EmptyState,
  TextButton,
} from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import React, { FunctionComponent, useContext } from 'react';
import DismissIcon from 'wix-ui-icons-common/Dismiss';

import { AttachmentCandidate } from '../';
import { Context } from '../../../../context';
import { DATA_HOOKS } from '../../../../dataHooks';
import { UploadType } from '../../../../types';

import css from './index.module.scss';

type UploadModalProps = {
  isOpen: boolean;
  hideModal: () => void;
  uploadType: UploadType;
  setAttachmentCandidate: (attachmentCandidate: AttachmentCandidate) => void;
};

export const UploadModal: FunctionComponent<UploadModalProps> = ({
  isOpen,
  hideModal,
  uploadType,
  setAttachmentCandidate,
}: UploadModalProps) => {
  const { t } = useTranslation();
  const { answersApi } = useContext(Context);
  const uploadFile = async (file: File) => {
    try {
      const { filename, filelink } = await answersApi.files.uploadFile({
        file,
        secured: true,
      });
      setAttachmentCandidate({
        filename,
        filelink,
      });
    } catch (e) {
      alert(`${t(`ticket.reply.upload.error.${uploadType}`)}`);
    } finally {
      hideModal();
    }
  };

  return (
    <Modal isOpen={isOpen} dataHook={DATA_HOOKS.UPLOAD_MODAL}>
      <Box className={css.modal} direction="vertical" gap="30px">
        <DismissIcon
          className={css.dismissIcon}
          onClick={hideModal}
          data-hook={DATA_HOOKS.UPLOAD_MODAL_CLOSE_BUTTON}
        />
        <Text className={css.title} dataHook={DATA_HOOKS.UPLOAD_MODAL_TITLE}>
          {t(`ticket.reply.upload.modal.title.${uploadType}`)}
        </Text>
        <Box direction="vertical" gap="16px">
          {uploadType === UploadType.FILE && (
            <Box direction="vertical">
              <Text
                className={css.fileNameTitle}
                dataHook={DATA_HOOKS.UPLOAD_MODAL_FILENAME_TITLE}
              >
                {t('ticket.reply.upload.modal.filename.title')}
              </Text>
              <Input className={css.fileNameInput} />
            </Box>
          )}
          <Dropzone
            onDrop={(files) => uploadFile(files[0])}
            dataHook={DATA_HOOKS.UPLOAD_MODAL_DROPZONE}
          >
            <Dropzone.Overlay>
              <Box className={css.dropzone} direction="vertical">
                <AddItem theme="filled" size="large">
                  {t('ticket.reply.upload.modal.drop.file.here')}
                </AddItem>
              </Box>
            </Dropzone.Overlay>
            <Dropzone.Content>
              <Box
                direction="vertical"
                className={`${css.dropzone} ${css.dropzonePadded}`}
              >
                <EmptyState
                  title={t('ticket.reply.upload.modal.title')}
                  subtitle={t('ticket.reply.upload.modal.subtitle')}
                >
                  <FileUpload
                    accept={`${uploadType === UploadType.IMAGE &&
                      'jpeg,jpg,png,gif,ico,bmp,tiff'
                      }`}
                    onChange={(fileList) => uploadFile((fileList as FileList)[0])}
                  >
                    {({ openFileUploadDialog }) => (
                      <TextButton onClick={openFileUploadDialog}>
                        {t('ticket.reply.upload.modal.choose.file')}
                      </TextButton>
                    )}
                  </FileUpload>
                </EmptyState>
              </Box>
            </Dropzone.Content>
          </Dropzone>
        </Box>
      </Box>
    </Modal>
  );
};
